import { formatINR } from "@creators/backend/lib/money";

export type OfferBoostTier = "basic" | "serious" | "priority";

/**
 * All-in prices charged at checkout (paise). GST is included in these amounts —
 * ₹49 / ₹199 are what the customer pays.
 */
export const OFFER_BOOST_TOTAL_PAISE: Record<Exclude<OfferBoostTier, "basic">, number> = {
  serious: 4_900, // ₹49 incl. GST
  priority: 19_900, // ₹199 incl. GST
};

/** GST rate used to split the inclusive total for invoice records (18%). */
export const OFFER_BOOST_GST_BPS = 1_800;

export function isOfferBoostTier(value: string): value is OfferBoostTier {
  return value === "basic" || value === "serious" || value === "priority";
}

/** Split a GST-inclusive total into taxable value + GST (paise). */
export function splitInclusiveGst(totalPaise: number, gstBps = OFFER_BOOST_GST_BPS): {
  basePaise: number;
  gstPaise: number;
  totalPaise: number;
} {
  if (totalPaise <= 0) return { basePaise: 0, gstPaise: 0, totalPaise: 0 };
  const basePaise = Math.round((totalPaise * 10_000) / (10_000 + gstBps));
  return { basePaise, gstPaise: totalPaise - basePaise, totalPaise };
}

export function boostPricing(tier: OfferBoostTier): {
  basePaise: number;
  gstPaise: number;
  totalPaise: number;
} {
  if (tier === "basic") return { basePaise: 0, gstPaise: 0, totalPaise: 0 };
  return splitInclusiveGst(OFFER_BOOST_TOTAL_PAISE[tier]);
}

/** Sort rank — lower first. Priority → Serious → Basic. */
export function boostSortRank(tier: string): number {
  if (tier === "priority") return 0;
  if (tier === "serious") return 1;
  return 2;
}

export function boostLabel(tier: string): string {
  if (tier === "priority") return "Priority";
  if (tier === "serious") return "Serious";
  return "Basic";
}

export function boostPaidSystemMessage(input: {
  tier: Exclude<OfferBoostTier, "basic">;
  basePaise: number;
  gstPaise: number;
  totalPaise: number;
}): string {
  // Tier is shown as a star/bolt icon in the UI — keep copy label-free.
  return `Offer fee paid: ${formatINR(input.totalPaise)} incl. GST. No refunds.`;
}
